import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { faPlus, faPencilAlt } from "@fortawesome/free-solid-svg-icons"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { Role } from "@/redux/role.types"
import NavApp from "@/nav/nav.app"
import PageHeader from "@/components/page-header"
import PagePlaceholder from "@/components/page-placeholder"
import Button from "@/components/button"
import Card from "@/components/card"
import ListButton from "@/components/list-button"
import RoleRulesWidget from "@/widgets/role-rules.widget"
import RoleUsersWidget from "@/widgets/role-users.widget"
import RoleEditModal from "@/modals/role-edit.modal"
import { roleDestroy, roleFetch, roleSave } from "@/redux/role.actions"
import PageLoader from "@/components/page-loader"
import { STATUS_LOADING } from "@/redux/_status.types"

function RestrictedAccessContent({ accountId }: { accountId: string }) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { roleId } = useParams()
  const rolesState = useAppSelector((state) => state.roles)
  const roles = rolesState.accountId === accountId ? rolesState.list : []
  const [roleToEdit, setRoleToEdit] = useState<Role | null | undefined>(undefined)
  const activeRole = roles.find((role) => role.id === roleId)

  function addRole(event?: { preventDefault: () => void }) {
    event?.preventDefault()
    setRoleToEdit(null)
  }

  async function saveRole(role: Role) {
    const savedId = await dispatch(roleSave(role, roleToEdit === null))
    if (!savedId) return
    setRoleToEdit(undefined)
    navigate(`/adm/restricted-access/${savedId}`)
  }

  async function deleteRole(role: Role) {
    if (!await dispatch(roleDestroy(role))) return
    setRoleToEdit(undefined)
    navigate("/adm/restricted-access", { replace: true })
  }

  if (rolesState.accountId !== accountId || rolesState.status === "idle" || rolesState.status === "loading") {
    return <PageLoader status={STATUS_LOADING} />
  }

  if (rolesState.status === "error") {
    return <PagePlaceholder title={t("utils_network_error")} button={t("roles_retry")} onClick={() => dispatch(roleFetch(true))} />
  }

  if (!activeRole && roles.length > 0) {
    return <Navigate to={`/adm/restricted-access/${roles[0].id}`} replace />
  }
  if (roleId && roles.length === 0) return <Navigate to="/adm/restricted-access" replace />

  return (
    <>
      {roleToEdit !== undefined && (
        <RoleEditModal
          role={roleToEdit ?? undefined}
          accountId={accountId}
          onClose={() => setRoleToEdit(undefined)}
          onSave={saveRole}
          onDelete={deleteRole}
        />
      )}
      <PageHeader title={t("roles_restricted_access")} subtitle={t("roles_restricted_access_help")}>
        {roles.length > 0 && (
          <Button className="primary" icon={faPlus} onClick={addRole}>
            {t("roles_add_role")}
          </Button>
        )}
      </PageHeader>

      {roles.length === 0 ? (
        <PagePlaceholder image="sherlock" title={t("roles_empty_list")} button={t("roles_add_role")} onClick={addRole} />
      ) : (
        activeRole && (
          <Card
            key={activeRole.id}
            isWithMinHeight
            title={
              <div className="flex">
                <span>{activeRole.name}</span>
                <ListButton icon={faPencilAlt} text={t("roles_edit_role")} onClick={() => setRoleToEdit(activeRole)} />
              </div>
            }>
            <RoleRulesWidget key={activeRole.id} role={activeRole} />
            <div className="mx-[-30px]">
              <RoleUsersWidget key={activeRole.id} role={activeRole} />
            </div>
          </Card>
        )
      )}
    </>
  )
}

export default function AdmRestrictedAccessRoute() {
  const session = useAppSelector((state) => state._session)

  if (session.userRole !== "ADMIN") return <Navigate to="/home" replace />

  if (!session.accountOptions.roleManagementEnabled) return <Navigate to="/adm/roles" replace />

  return (
    <NavApp>
      <RestrictedAccessContent key={session.accountId} accountId={session.accountId} />
    </NavApp>
  )
}
